import DetectRTC from 'detectrtc';
import {
  ConnectionStatus,
  VoiceChannel,
} from 'src/provider/VoiceChannel/voice-channel-context.types';
import { create } from 'zustand';

import {
  connectOpts,
  initialParticipant,
  initialPreConnectionOptions,
  roomOpts,
} from 'src/provider/VoiceChannel/livekit/constants';

import {
  AiStatus,
  GuestRequestType,
  PreConnectionOptionsType,
  RTCConnection,
  VideoFilterParamsType,
  VideoFilterType,
  VoiceChannelParticipantLocal,
  VoiceChannelParticipantRemote,
} from 'src/provider/VoiceChannel/livekit/types';

import {
  ConnectionQuality,
  LocalAudioTrack,
  LocalVideoTrack,
  Participant,
  ParticipantEvent,
  Room,
  RoomEvent,
  Track,
  createLocalVideoTrack,
  setLogLevel,
} from 'livekit-client';
import { LIVEKIT_SERVER_URL } from 'src/config-global';
import { AudioType } from 'src/features/audio';

import {
  KrispNoiseFilter,
  isKrispNoiseFilterSupported,
} from '@livekit/krisp-noise-filter';
import { getVideoFilter } from './utils';

const krispProcessor = KrispNoiseFilter();

setLogLevel('silent');

interface RTCState {
  // PRE-CONNECT OBJECT / OPTIONS
  connectionObject: RTCConnection | null;
  setConnectionObject: (connectionObject: RTCConnection | null) => void;
  preConnectionOptions: PreConnectionOptionsType;
  setPreConnectionOptions: (options: Partial<PreConnectionOptionsType>) => void;
  // RTC Objects
  rtcConnection: RTCConnection | null;
  room: Room | null;
  activeVoiceChannel: VoiceChannel;
  setActiveVoiceChannel: (activeVoiceChannel: VoiceChannel) => void;
  activeSessionId: string | null;
  setActiveSessionId: (sessionId: string | null) => void;

  // PARTICIPANTS
  localParticipant: VoiceChannelParticipantLocal;
  remoteParticipants: Record<string, VoiceChannelParticipantRemote>;
  updateLocalParticipant: (
    param: Partial<VoiceChannelParticipantLocal>,
  ) => void;
  updateRemoteParticipant: (
    participantId: string,
    param: Partial<VoiceChannelParticipantRemote>,
  ) => void;
  lobbyGuestsRequested: GuestRequestType[];
  addLobbyGuest: (guest: GuestRequestType) => void;
  removeLobbyGuest: (guest: GuestRequestType) => void;

  // Participant Options
  myMeetId: string | undefined;
  isJoined: boolean;
  localDevices: string[];
  defaultDevice: {
    input: string;
    output: string;
    video: string;
  };
  updateDefaultDevice: (
    params: Partial<{
      input: string;
      output: string;
      video: string;
    }>,
  ) => void;

  // Connection Objects
  connectionStatus: ConnectionStatus;
  setConnectionStatus: (status: ConnectionStatus) => void;
  connectionQuality: ConnectionQuality;
  setConnectionQuality: (quality: ConnectionQuality) => void;

  // RTC Events
  isAlive: () => void;
  reconnect: () => void;
  timeout: NodeJS.Timeout | null;

  // RTC Functions
  createConnection: (serverURL?: string) => void;
  onConnectionFailed: () => void;
  disconnected: () => void;

  // Voice Control Functions
  participantConnected: (participant: Participant) => void;
  renderParticipant: (participant: Participant, remove?: boolean) => void;

  disconnect: (reconnecting?: boolean) => void;

  setMute: (mute: boolean) => void;
  setDeaf: (deaf: boolean) => void;
  switchVideo: (type?: string) => void;
  stopVideo: (type?: string) => void;
  toggleHandRaised: () => void;

  theaterMember: string | null;
  setTheaterMember: (meetId: string | null) => void;
  activeMember: string | null;
  setActiveMember: (meetId: string | null) => void;
  viewMode: 'grid' | 'list';
  setViewMode: (viewMode: 'grid' | 'list') => void;

  changeDevice: (kind: MediaDeviceKind, deviceId: string) => void;
  audioPlay: AudioType | null;
  setAudioPlay: (audioPlay: AudioType | null) => void;
  textToSpeech: (text: string) => void;

  aiActive: boolean;
  aiStatus: AiStatus;
  setAiStatus: (aiStatus: AiStatus) => void;
  channelLockedBy: string | null;
  setChannelLockedBy: (meetId: string | null) => void;

  videoFilter: VideoFilterType;
  setVideoFilter: (filter: VideoFilterType) => void;
  filterParams: VideoFilterParamsType;
  setFilterParams: (
    filter: VideoFilterType,
    newParam: Partial<VideoFilterParamsType>,
  ) => void;
  setProcessor: (filter: VideoFilterType, stopProcessor?: boolean) => void;
  noiseFilter: boolean;
  setNoiseFilter: (noiseFilter: boolean) => void;
}

export const rtcState = create<RTCState>((set, get) => ({
  connectionObject: null,
  setConnectionObject: (connectionObject) => set({ connectionObject }),

  preConnectionOptions: initialPreConnectionOptions,
  setPreConnectionOptions: (options) =>
    set((state) => ({
      preConnectionOptions: {
        ...state.preConnectionOptions,
        ...options,
      },
    })),

  rtcConnection: null,
  connection: null,
  room: null,
  activeVoiceChannel: {} as VoiceChannel,
  setActiveVoiceChannel: (activeVoiceChannel: VoiceChannel) =>
    set({ activeVoiceChannel }),
  activeSessionId: null,
  setActiveSessionId: (activeSessionId: string | null) =>
    set({ activeSessionId }),
  localParticipant: initialParticipant as VoiceChannelParticipantLocal,
  remoteParticipants: {},

  updateLocalParticipant: (param) => {
    set((state) => ({
      localParticipant: {
        ...(state.localParticipant || {}),
        ...param,
      },
    }));
  },

  updateRemoteParticipant: (participantId, param) => {
    set((state) => ({
      remoteParticipants: {
        ...(state.remoteParticipants || {}),
        [participantId]: {
          ...(state.remoteParticipants[participantId] || {}),
          ...param,
        },
      },
    }));
  },

  lobbyGuestsRequested: [],
  addLobbyGuest: (guest: GuestRequestType) => {
    set((state) => ({
      lobbyGuestsRequested: [...state.lobbyGuestsRequested, guest],
    }));
  },
  removeLobbyGuest: (guest: GuestRequestType) => {
    set((state) => ({
      lobbyGuestsRequested: [
        ...state.lobbyGuestsRequested.filter(
          (lobbyGuest) => lobbyGuest.deviceId !== guest.deviceId,
        ),
      ],
    }));
  },

  myMeetId: undefined,
  isJoined: false,
  localDevices: ['audio'],
  defaultDevice: {
    input: 'default',
    output: 'default',
    video: 'default',
  },
  updateDefaultDevice: (params) => {
    const { defaultDevice } = get();
    set({ defaultDevice: { ...defaultDevice, ...params } });
  },

  connectionStatus: 'disconnected',
  setConnectionStatus: (status) => {
    set({ connectionStatus: status });
  },
  connectionQuality: ConnectionQuality.Excellent,
  setConnectionQuality: (quality) => {
    set({ connectionQuality: quality });
  },

  isAlive: () => {
    set({ connectionStatus: 'connected' });
    // set({ connectionStatus: 'reconnected' });
  },
  reconnect: () => {
    set({ connectionStatus: 'reconnecting' });
  },

  timeout: null,

  createConnection: async (serverURL = LIVEKIT_SERVER_URL) => {
    const {
      connectionObject,
      disconnected,
      timeout,
      participantConnected,
      renderParticipant,
      setAudioPlay,
    } = get();
    if (timeout) {
      clearTimeout(timeout);
    }
    if (!connectionObject?.token) return;
    if (get().rtcConnection?.roomId !== connectionObject.roomId) {
      set({
        rtcConnection: connectionObject,
        activeMember: null,
        theaterMember: null,
      });
    }
    const { rtcConnection } = get();
    if (!rtcConnection) return;

    set({ connectionStatus: 'authenticating' });

    const room = new Room(roomOpts);

    room
      .on(RoomEvent.Connected, () => {
        const { preConnectionOptions, setMute, switchVideo } = get();
        const myMeetId = room.localParticipant.sid;

        room.remoteParticipants.forEach((participant) => {
          participantConnected(participant);
        });

        participantConnected(room.localParticipant);

        set({
          room,
          myMeetId,
          isJoined: true,
          connectionStatus: 'connected',
        });

        setAudioPlay(AudioType.CHANNEL_VOICE_ENTERING);

        if (!preConnectionOptions.micOn) {
          setMute(true);
        }

        if (preConnectionOptions.cameraOn) {
          switchVideo('camera');
        }
      })
      .on(RoomEvent.ParticipantConnected, (participant) => {
        participantConnected(participant);
        if (participant.isAgent) return;
        setAudioPlay(AudioType.CHANNEL_VOICE_ENTERING);
      })
      .on(RoomEvent.ParticipantDisconnected, (participant) => {
        const {
          remoteParticipants,
          theaterMember,
          setTheaterMember,
          activeMember,
          setActiveMember,
          myMeetId,
        } = get();

        if (participant.sid === theaterMember) {
          setTheaterMember(null);
        }
        if (participant.sid === activeMember) {
          const remoteSIDs = Object.keys(remoteParticipants);
          const newActiveMember = (remoteSIDs.find(
            (sid) =>
              sid !== participant.sid &&
              !remoteParticipants[sid].identity?.startsWith('agent'),
          ) || myMeetId) as string;
          setActiveMember(newActiveMember);
        }

        delete remoteParticipants[participant.sid];
        set({ remoteParticipants });
        // renderParticipant(participant, true);
        if (participant.isAgent) return;
        setAudioPlay(AudioType.CHANNEL_VOICE_LEAVING);
      })
      // .on(RoomEvent.DataReceived, (e) => console.log('=====> DataReceived', e))
      .on(RoomEvent.Disconnected, disconnected)
      .on(RoomEvent.Reconnecting, () =>
        set({ connectionStatus: 'reconnecting' }),
      )
      .on(RoomEvent.Reconnected, async () => {
        // const { timeout } = get();
        if (!timeout) return;
        clearTimeout(timeout);
        // setTimeout(() => {
        get().isAlive();
        // }, 3000);
      })
      .on(RoomEvent.LocalTrackPublished, async (trackPublication) => {
        if (
          trackPublication.source === Track.Source.Microphone &&
          trackPublication.track instanceof LocalAudioTrack &&
          isKrispNoiseFilterSupported() &&
          !DetectRTC.isMobileDevice &&
          get().noiseFilter
        ) {
          const processor = trackPublication.track.getProcessor();
          if (!processor) {
            await trackPublication.track.setProcessor(krispProcessor);
            await krispProcessor.setEnabled(true);
          }
        }
        renderParticipant(room.localParticipant);
      })
      .on(RoomEvent.LocalTrackUnpublished, async (trackPublication) => {
        if (
          trackPublication.source === Track.Source.Microphone &&
          trackPublication.track instanceof LocalAudioTrack
        ) {
          const processor = trackPublication.track.getProcessor();
          if (processor) {
            await trackPublication.track.stopProcessor();
            await krispProcessor.setEnabled(false);
          }
        }
        renderParticipant(room.localParticipant);
      })
      // .on(RoomEvent.RoomMetadataChanged, (metadata) => {
      //   console.log('=====> New metadata for room', metadata);
      // })
      .on(RoomEvent.MediaDevicesChanged, () => DetectRTC.load(() => {}))
      // .on(RoomEvent.ActiveDeviceChanged, () => console.log('ActiveDeviceChanged'))
      // .on(RoomEvent.AudioPlaybackStatusChanged, () => {
      //   console.log('=====> AudioPlaybackStatusChanged', room.canPlaybackAudio)
      // })
      // .on(RoomEvent.MediaDevicesError, (e: Error) => {
      //   console.log('=====> MediaDevicesError')
      //   const failure = MediaDeviceFailure.getFailure(e);
      //   console.log('media device failure', failure);
      // })
      // .on(
      //   RoomEvent.ConnectionQualityChanged,
      //   (quality: ConnectionQuality, participant?: Participant) =>
      //   {
      //     console.log('=====> Connection quality changed', participant?.identity, quality);
      //   },
      // )
      // .on(RoomEvent.TrackPublished, (pub, participant) => {
      //   console.log('TrackPublished =>');
      //   renderParticipant(participant);
      // })
      // .on(RoomEvent.TrackUnpublished, (pub, participant) => {
      //   console.log('TrackUnpublished =>');
      //   renderParticipant(participant);
      // })
      .on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
        // console.log('TrackSubscribed =>');
        renderParticipant(participant);
      })
      .on(RoomEvent.TrackUnsubscribed, (_, pub, participant) => {
        // console.log('TrackUnsubscribed =>');
        renderParticipant(participant);
      })
      .on(RoomEvent.SignalConnected, async () => {
        await room.localParticipant.setMicrophoneEnabled(true);
      })
      .on(
        RoomEvent.ActiveSpeakersChanged,
        async (participants: Participant[]) => {
          if (!participants.length) return;
          if (participants?.[0]?.isLocal || participants?.[0]?.isAgent) return;
          get().setActiveMember(participants[0]?.sid);
        },
      );
    // .on(RoomEvent.ParticipantEncryptionStatusChanged, () => {
    //   console.log('=====> ParticipantEncryptionStatusChanged')
    //   // updateButtonsForPublishState();
    // })
    // .on(RoomEvent.TrackStreamStateChanged, (pub, streamState, participant) => {
    //   console.log(
    //     `=====> stream state changed for ${pub.trackSid} (${
    //       participant.identity
    //     }) to ${streamState.toString()}`,
    //   );
    // });

    try {
      await room.prepareConnection(serverURL, connectionObject.token);
      await room.connect(serverURL, connectionObject.token, connectOpts);
    } catch (e) {
      get().onConnectionFailed();
    }
  },

  onConnectionFailed: () => {
    set({ isJoined: false, connectionStatus: 'error', aiActive: false });
    get().reconnect();
  },

  disconnected: () => {
    get().setAudioPlay(AudioType.CHANNEL_VOICE_LEAVING);
    set({
      preConnectionOptions: initialPreConnectionOptions,
      localParticipant: initialParticipant as VoiceChannelParticipantLocal,
      remoteParticipants: {},
      aiActive: false,
      theaterMember: null,
      activeMember: null,
      viewMode: 'grid',
    });

    if (get().connectionStatus === 'nodisconnect') return;

    set({
      connectionObject: null,
      activeVoiceChannel: {} as VoiceChannel,
      activeSessionId: null,
      room: undefined,
      connectionStatus: 'disconnected',
    });
  },

  participantConnected: async (participant) => {
    const { renderParticipant } = get();

    set({ aiActive: true });
    // if (participant.isAgent) {
    //   set({ aiActive: true });
    // }

    const setMuted = (muted: boolean) => {
      if (participant.isLocal) {
        get().updateLocalParticipant({
          muted,
        });
      } else {
        const { remoteParticipants } = get();
        if (!remoteParticipants[participant.sid]) return;
        get().updateRemoteParticipant(participant.sid, {
          muted,
        });
      }
    };

    participant
      .on(ParticipantEvent.TrackMuted, () => {
        setMuted(true);
      })
      .on(ParticipantEvent.TrackUnmuted, () => {
        setMuted(false);
      })
      .on(ParticipantEvent.IsSpeakingChanged, () => {
        // TODO - ver se conseguimos tirar esse render daqui
        renderParticipant(participant);

        if (participant.isAgent && get().channelLockedBy === get().myMeetId) {
          if (participant.isSpeaking) {
            set({ aiStatus: 'speaking' });
          }
        }
        if (!participant.isLocal) return;
        if (get().localParticipant.raisedHand) {
          get().toggleHandRaised();
        }
      })
      .on(ParticipantEvent.ConnectionQualityChanged, () => {
        renderParticipant(participant);
        if (participant.isLocal) {
          set({ connectionQuality: participant.connectionQuality });
        }
      });
  },
  renderParticipant: async (participant: Participant, remove = false) => {
    const { setAudioPlay } = get();

    const micPub = participant.getTrackPublication(Track.Source.Microphone);

    if (participant.isLocal) {
      get().updateLocalParticipant({
        identity: participant.identity,
        muted: micPub?.isMuted,
        sharing: participant.isScreenShareEnabled,
        camera: participant.isCameraEnabled,
      });
    } else {
      const participantInfo = get().remoteParticipants[participant.sid];
      const hasCamera = participantInfo?.camera;
      const startCamera = !hasCamera && participant.isCameraEnabled;
      const stopCamera = hasCamera && !participant.isCameraEnabled;

      const isSharing = participantInfo?.sharing;
      const startSharing = !isSharing && participant.isScreenShareEnabled;
      const stopSharing = isSharing && !participant.isScreenShareEnabled;

      const volume = participantInfo?.volume || 1;
      const localMuted = participantInfo?.localMuted || false;

      if (startSharing || startCamera) {
        setAudioPlay(AudioType.CHANNEL_VOICE_SCREEN_SHARING_START);
      }
      if (stopSharing || stopCamera) {
        setAudioPlay(AudioType.CHANNEL_VOICE_SCREEN_SHARING_END);
      }

      get().updateRemoteParticipant(participant.sid, {
        identity: participant.identity,
        muted: micPub?.isMuted || false,
        sharing: participant.isScreenShareEnabled,
        camera: participant.isCameraEnabled,
        volume,
        localMuted,
      });
    }
  },
  disconnect: async (reconnecting = false) => {
    const { room, localParticipant, stopVideo } = get();

    if (localParticipant.sharing) {
      stopVideo();
    }

    if (localParticipant.camera) {
      stopVideo('video');
    }

    if (room) {
      try {
        if (reconnecting) {
          set({ connectionStatus: 'nodisconnect', aiActive: false });
        }
        await room.disconnect();
        set({
          isJoined: false,
          lobbyGuestsRequested: [],
        });
        if (reconnecting) {
          set({ connectionStatus: 'reconnecting' });
        }
      } catch (error) {
        console.error(error);
      }
      set({ rtcConnection: {} });
    }
  },
  setMute: async (muted) => {
    // DONE
    const { room, updateLocalParticipant } = get();
    if (!room) return;
    await room.localParticipant.setMicrophoneEnabled(!muted);
    updateLocalParticipant({ muted });
  },
  setDeaf: (deaf) => {
    const { updateLocalParticipant } = get();
    updateLocalParticipant({ deaf });
  },
  switchVideo: async (type = 'desktop') => {
    const { room, stopVideo, videoFilter, filterParams, defaultDevice } = get();
    if (!room) return;

    stopVideo(type);

    const resolution = {
      width: 1600,
      height: 900,
      aspectRatio: 1.7777777777777777,
      frameRate: 15,
      maxBitrate: 1_500_000,
    };

    if (type === 'desktop') {
      await room.localParticipant.setScreenShareEnabled(true, {
        audio: true,
        resolution,
      });
    } else {
      get().updateLocalParticipant({
        camera: true,
      });
      try {
        const track = await createLocalVideoTrack({
          deviceId: defaultDevice.video,
          resolution,
          processor: videoFilter
            ? getVideoFilter(videoFilter, filterParams)
            : undefined,
        });
        await room.localParticipant.publishTrack(track);
      } catch (e: any) {
        console.error(e);
        get().updateLocalParticipant({
          camera: false,
        });
      }
    }
  },
  stopVideo: async (type = 'desktop') => {
    const { room, updateLocalParticipant } = get();
    if (!room) return;

    updateLocalParticipant({
      sharing: false,
      camera: false,
    });

    const localVideoTrack = room.localParticipant.getTrackPublication(
      type === 'desktop' ? Track.Source.ScreenShare : Track.Source.Camera,
    )?.videoTrack;
    if (localVideoTrack) {
      room.localParticipant.unpublishTrack(localVideoTrack);
    }

    if (type === 'desktop') {
      await room.localParticipant.setScreenShareEnabled(false, { audio: true });
    } else {
      await room.localParticipant.setCameraEnabled(false);
    }
  },
  toggleHandRaised: () => {
    const { updateLocalParticipant, localParticipant } = get();
    updateLocalParticipant({ raisedHand: !localParticipant.raisedHand });
  },
  theaterMember: null,
  setTheaterMember: (theaterMember) => set({ theaterMember }),
  activeMember: null,
  setActiveMember: (activeMember) => set({ activeMember }),
  viewMode: 'grid',
  setViewMode: (viewMode) => {
    set({ viewMode });
    const { theaterMember, setTheaterMember } = get();
    if (viewMode === 'grid' && theaterMember) {
      setTheaterMember(null);
    }
  },
  changeDevice: (kind: MediaDeviceKind, deviceId: string) => {
    const { room, updateDefaultDevice } = get();
    if (!room) return;
    room.switchActiveDevice(kind, deviceId);

    let params;
    switch (kind) {
      case 'audioinput':
        params = { input: deviceId };
        break;
      case 'audiooutput':
        params = { output: deviceId };
        break;
      default:
        params = { video: deviceId };
    }

    updateDefaultDevice(params);
  },
  audioPlay: null,
  setAudioPlay: (audioPlay) => {
    set({ audioPlay });
    setTimeout(() => set({ audioPlay: null }), 200);
  },
  textToSpeech: (speechText: string) => {
    const { room } = get();
    if (!room) return;
    const strData = JSON.stringify({ speechText });
    const encoder = new TextEncoder();
    const data = encoder.encode(strData);
    room.localParticipant.publishData(data, { reliable: false });
  },
  aiActive: true,
  aiStatus: null,
  setAiStatus: (aiStatus) => {
    set({ aiStatus });
  },
  channelLockedBy: null,
  setChannelLockedBy: (meetId) => {
    if (!meetId && get().channelLockedBy === get().myMeetId) {
      set({ aiStatus: null });
    }
    set({ channelLockedBy: meetId });
  },
  videoFilter: null,
  setVideoFilter: async (filter) => {
    const { room, videoFilter, filterParams, setProcessor } = get();

    set({ videoFilter: filter });

    if (!room) return;

    const { isCameraEnabled } = room.localParticipant;

    if (videoFilter === filter || !isCameraEnabled) return;

    const hasFilter = filter === 'blur' || !!filterParams.virtualBackground;

    if (hasFilter) {
      setProcessor(filter, !!videoFilter || !filter);
    }
  },
  filterParams: {
    blurRadius: 5,
    blurDelegate: 'GPU',
    virtualBackground: '',
  },
  setFilterParams: (filter, newParam) => {
    const { room, videoFilter, setProcessor, filterParams } = get();

    set({
      filterParams: {
        ...filterParams,
        ...newParam,
      },
    });

    if (!room) return;

    const { isCameraEnabled } = room.localParticipant;

    if (videoFilter === filter && isCameraEnabled) {
      setProcessor(videoFilter);
    }
  },
  setProcessor: async (filter, stopProcessor = true) => {
    const { room, filterParams } = get();
    if (!room) return;

    const camTrack = room.localParticipant.getTrackPublication(
      Track.Source.Camera,
    )!.videoTrack as LocalVideoTrack;

    if (camTrack) {
      if (stopProcessor) {
        await camTrack.stopProcessor();
      }

      if (!filter) return;

      const newFilter = getVideoFilter(filter, filterParams);

      await camTrack.setProcessor(newFilter);
    }
  },
  noiseFilter: true,
  setNoiseFilter: async (noiseFilter: boolean) => {
    set({ noiseFilter });

    const { room } = get();
    if (!room) return;

    const localAudioTrack = room.localParticipant.getTrackPublication(
      Track.Source.Microphone,
    )!.audioTrack as LocalAudioTrack;

    const processor = localAudioTrack.getProcessor();

    if (!noiseFilter) {
      if (!processor) return;
      await localAudioTrack.stopProcessor();
      await krispProcessor.setEnabled(false);
    } else {
      if (processor) return;
      await localAudioTrack.setProcessor(krispProcessor);
      await krispProcessor.setEnabled(true);
    }
  },
}));

export const useLivekitRTCState = rtcState;
